import Box from "@mui/material/Box";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { colors } from "@/theme/colors";

const AUTO_SLIDE_INTERVAL_MS = 5000;

type CarouselProps = {
  /** スライドに表示する画像 URL のリスト。 */
  images: string[];
  /** 左上に表示するピル型ラベル (任意)。 */
  children?: ReactNode;
  /** 高さ指定 (デフォルト: { xs: 220, sm: 320, md: 400 })。 */
  height?: number | Record<string, number>;
};

/**
 * 画像スライドショーコンポーネント。
 * - 複数画像をスライド形式で表示する
 * - 5 秒ごとに自動でスライドする
 * - 中央下部にドット型インジケーターを表示する
 * - children で左上にピル型ラベルを表示するスペースを提供する
 */
export function Carousel({
  images,
  children,
  height = { xs: 220, sm: 320, md: 400 },
}: CarouselProps) {
  const [current, setCurrent] = useState(0);

  // images.length が変化した際 (例: 3枚→1枚) に current が範囲外のまま残ると
  // transform 計算がズレて表示が崩れるため、都度 0..length-1 にクランプする。
  useEffect(() => {
    setCurrent((prev) => (images.length === 0 ? 0 : Math.min(prev, images.length - 1)));
  }, [images.length]);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, AUTO_SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [images.length]);

  if (images.length === 0) return null;

  return (
    <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
      {/* スライド本体 */}
      <Box
        sx={{
          display: "flex",
          width: `${images.length * 100}%`,
          transform: `translateX(-${(current / images.length) * 100}%)`,
          transition: "transform .35s ease",
          height,
        }}
      >
        {images.map((src, i) => (
          <Box
            key={src}
            component="img"
            src={src}
            alt={`slide-${i + 1}`}
            sx={{
              width: `${100 / images.length}%`,
              height: "100%",
              objectFit: "cover",
              flexShrink: 0,
            }}
          />
        ))}
      </Box>

      {/* 左上ピル型ラベル */}
      {children && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {children}
        </Box>
      )}

      {/* インジケーター (中央下部) */}
      {images.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 0.75,
          }}
        >
          {images.map((_, i) => (
            <Box
              key={`dot-${
                // biome-ignore lint/suspicious/noArrayIndexKey: スライド位置に対応するため index をキーにする
                i
              }`}
              component="button"
              type="button"
              aria-label={`Slide ${i + 1}`}
              onClick={() => setCurrent(i)}
              sx={{
                width: i === current ? 20 : 8,
                height: 8,
                borderRadius: 4,
                border: "none",
                bgcolor: i === current ? colors.gold : "rgba(255,255,255,.6)",
                cursor: "pointer",
                p: 0,
                transition: "width .2s, background-color .2s",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
